import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import { Entity, ProcedureType, IUseQueryPaged, PagedResponse, QueryHookParams, QueryType } from "../models";
import { Operation } from "rfc6902";

interface IUseProcedureTypeQuery
    extends Pick<IUseQueryPaged<ProcedureType>, "listQuery" | "detailQuery" | "updateMutation"> {}

export const useProcedureTypeQuery = (params?: QueryHookParams): IUseProcedureTypeQuery => {
    const procedureTypeId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, patch } = useAxios();
    const { queryParams, sortParams } = useQueryParams().getParamsApi(Entity.ProcedureTypes);
    const url = Endpoints.ProcedureTypes;
    const queryType = getQueryType(procedureTypeId, params?.queryType);

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<ProcedureType>>(
                getRequestOptions(url, config, queryParams, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.procedureTypes.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<ProcedureType>({ url: `${url}/${procedureTypeId}`, config }),
        queryKey: queryKeys.procedureTypes.detail(procedureTypeId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) =>
            patch<Operation[], ProcedureType>({ url: `${url}/${procedureTypeId}`, data }),
        queryKey: queryKeys.procedureTypes.detail(procedureTypeId),
        keysToInvalidate: [queryKeys.procedureTypes.lists(), queryKeys.procedureTypes.pagedLists()],
        successToast: "Procedure type updated",
    });

    return useMemo<IUseProcedureTypeQuery>(
        () => ({
            listQuery,
            detailQuery,
            updateMutation,
        }),
        [listQuery, detailQuery, updateMutation]
    );
};
